import { useListaCboApi } from "../../../infra/geo-veles/lista-cbo";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { useUIService } from "../../ui";
import { useCallback } from "react";
import {
  resetCreateListaCboFormState,
  resetEditListaCboFormState,
  resetListaCboFileDataState,
  setCreateListaCboFormErrorsState,
  setCreateListaCboFormTouchedState,
  setCreateListaCboIdUsuarioState,
  setCreateListaCboNomeState,
  setEditListaCboIdUsuarioState,
  setEditListaCboIdVelesGeoListaCboState,
  setEditListaCboNomeState,
  setEditListaCboState,
  setListaCboState,
  setOpenCreateListaCboState,
  setOpenDialogListaCboState,
  setOpenEditListaCboState,
  setOpenImportFileCboState,
  setOpenPreviewListaCboState,
  validateCreateListaCboForm,
  setListaCboFileValidDataListState,
  setListaCboFileInvalidDataListState,
  setListaCboFileRepeatedDataListState,
} from "../../../application/states/geo-veles/lista-cbo";
import { IListaCbo } from "../../../application/models/geo-veles/IListaCbo";
import { setLoadingState } from "../../../application/states/ui";

export const useListaCboService = () => {
  const dispatch = useAppDispatch();

  const {
    user: { currentUser },
    listaCbo: listaCboState,
  } = useAppSelector((state) => state);

  const { setAlert } = useUIService();

  const {
    createListaCboApi,
    updateListaCboApi,
    updateStatusListaCboApi,
    getByIdListaCboApi,
    getByIdClienteListaCboApi,
  } = useListaCboApi();

  const getListaCboById = useCallback(
    async (idLista: number): Promise<IListaCbo> => {
      try {
        const rawLista = await getByIdListaCboApi(idLista); // já corrigido para retornar o item

        // Parse seguro de CBOs
        let cbosArray: string[] | null = null;
        if (rawLista.CBOs && typeof rawLista.CBOs === 'string') {
          try {
            cbosArray = JSON.parse(rawLista.CBOs);
          } catch (e) {
            console.warn('Falha ao parsear CBOs:', rawLista.CBOs);
            cbosArray = null;
          }
        }

        const lista: IListaCbo = {
          IdVelesGeoListaCBO: rawLista.IdVelesGeoListaCBO,
          IdUsuario: rawLista.IdUsuario, // necessário pela interface
          Nome: rawLista.Nome,
          CBOs: cbosArray, // agora é string[] | null
          Ativa: rawLista.Ativa, // necessário pela interface
        };

        return lista;
      } catch (error) {
        console.error("Erro ao buscar lista CBO com ID", idLista, ":", error);
        return Promise.reject(error);
      }
    },
    [getByIdListaCboApi]
  );

  const setupListasCbo = useCallback(async () => {
    if (!currentUser?.IdCliente) {
      console.warn("currentUser ou IdCliente ausente");
      return;
    }

    try {
      const response: any = await getByIdClienteListaCboApi(
        currentUser.IdCliente
      );
      const listasBasicas = Array.isArray(response.ListaCbo) ? response.ListaCbo : [];

      const listasCompletas = await Promise.all(
        listasBasicas.map(async (lista: any) => {
          try {
            const listaCompleta = await getListaCboById(lista.IdVelesGeoListaCBO);
            return listaCompleta;
          } catch (error) {
            console.warn(`⚠️ [useListaCboService] Erro ao carregar CBOs da lista ${lista.Nome}:`, error);
            return lista;
          }
        })
      );


      dispatch(setListaCboState({ listasCbo: listasCompletas }));
    } catch (error: any) {
      console.error("❌ [useListaCboService] Erro ao carregar listas CBO:", error);
      setAlert({
        message:
          error?.response?.data?.error ?? "Erro ao carregar listas de CBO",
        type: "error",
      });
    }
  }, [currentUser, dispatch, setAlert, getByIdClienteListaCboApi, getListaCboById]);


  //PREVIEW
  const setOpenListaCboDialog = useCallback(
    (openDialog: boolean) => {
      dispatch(setOpenDialogListaCboState(openDialog));
    },
    [dispatch]
  );

  //CREATE
  const setCreateListaCbosNome = useCallback(
    (name: string) => {
      dispatch(
        setCreateListaCboNomeState({
          nome: name,
        })
      );
    },
    [dispatch]
  );

  const setCreateListaCboFormIdUsuario = useCallback(
    (idUsuario: number) => {
      dispatch(
        setCreateListaCboIdUsuarioState({
          idUsuario,
        })
      );
    },
    [dispatch]
  );

  const createListaCbos = useCallback(async () => {
    if (!currentUser) {
      console.error("Usuário não autenticado");
      setAlert({
        message: "Usuário não autenticado",
        type: "error"
      });
      return null;
    }

    const loadingId = Math.random().toString();
    try {
      const errors = validateCreateListaCboForm(
        listaCboState.createListaCboForm.fields
      );

      if (errors.length > 0) {
        dispatch(setCreateListaCboFormErrorsState({ errors }));
        dispatch(setCreateListaCboFormTouchedState({ touched: true }));
        return null;
      }

      setLoadingState({
        id: loadingId,
        loading: true,
        message: "Criando lista de CBO...",
      });

      const payload = {
        IdUsuario: currentUser.IdUsuario,
        Nome: listaCboState.createListaCboForm.fields.Nome,
      };

      const novaLista = await createListaCboApi(payload);

      if (!novaLista || !novaLista.IdVelesGeoListaCBO) {
        throw new Error("Resposta inválida da API: lista CBO não retornada");
      }

      dispatch(
        setListaCboState({
          listasCbo: [...listaCboState.listasCbo, novaLista],
        })
      );

      setAlert({
        message: "Lista CBO criada com sucesso!",
        type: "success"
      });
      return novaLista;

    } catch (error) {
      setAlert({
        message: "Já existe uma lista com este nome.",
        type: "error"
      });
      return null;

    } finally {
      setLoadingState({
        id: loadingId,
        loading: false,
        message: "",
      });
    }
  }, [currentUser, listaCboState, dispatch, createListaCboApi]);

  const openCreateListaCboForm = useCallback(
    (openForm: boolean) => {
      if (openForm) {
        if (!currentUser) {
          console.error("Tentativa de abrir formulário sem usuário logado");
          return;
        }

        dispatch(
          setCreateListaCboIdUsuarioState({
            idUsuario: currentUser.IdUsuario,
          })
        );
      } else {
        dispatch(resetCreateListaCboFormState());
      }

      dispatch(setOpenCreateListaCboState(openForm));
    },
    [dispatch, currentUser]
  );

  //Edit
  const setEditListaCboNome = useCallback(
    (name: string) => {
      dispatch(
        setEditListaCboNomeState({
          nome: name,
        })
      );
    },
    [dispatch]
  );

  const setEditListaCboFields = useCallback(
    (data: { listaCbo: IListaCbo }) => {
      const { IdVelesGeoListaCBO, IdUsuario, Nome, CBOs } = data.listaCbo;

      dispatch(
        setEditListaCboIdVelesGeoListaCboState({
          idVelesGeoListaCbo: IdVelesGeoListaCBO,
        })
      );
      dispatch(
        setEditListaCboIdUsuarioState({
          idUsuario: IdUsuario,
        })
      );
      dispatch(
        setEditListaCboNomeState({
          nome: Nome || "",
        })
      );
      dispatch(
        setEditListaCboState({
          cbos: CBOs ?? [],
        })
      );
    },
    [dispatch]
  );

  const selectedListaCboToEdit = useCallback(
    (IdVelesGeoListaCbo: number) => {
      const listaCboToEdit = listaCboState.listasCbo.find(
        (item) => item.IdVelesGeoListaCBO === IdVelesGeoListaCbo
      );
      if (listaCboToEdit) {
        setEditListaCboFields({
          listaCbo: listaCboToEdit,
        });
      }
    },
    [listaCboState.listasCbo, setEditListaCboFields]
  );

  const openEditListaCboForm = useCallback(
    (lista: IListaCbo) => {
      setEditListaCboFields({ listaCbo: lista });
      dispatch(setOpenEditListaCboState(true));
    },
    [dispatch, setEditListaCboFields]
  );

  const closeEditListaCbo = useCallback(() => {
    dispatch(setOpenEditListaCboState(false));
  }, [dispatch]);

  const updateListaCbo = useCallback(async () => {
    if (!currentUser?.IdUsuario) {
      setAlert({
        message: "Usuário não autenticado",
        type: "error"
      });
      return;
    }

    const { fields } = listaCboState.editListaCboForm;

    if (!fields.IdVelesGeoListaCbo) {
      setAlert({
        message: "ID da lista CBO inválido",
        type: "error"
      });
      return;
    }

    const nomeValidado = fields.Nome?.trim();
    if (!nomeValidado) {
      setAlert({
        message: "O nome da lista é obrigatório",
        type: "error"
      });
      return;
    }

    // ✅ Usa diretamente os CBOs do formulário (já incluem os importados)
    const finalCBOs = fields.CBOs || [];

    const loadingId = Math.random().toString();
    try {
      setLoadingState({
        id: loadingId,
        loading: true,
        message: "Salvando alterações...",
      });

      const payload = {
        idVelesGeoListaCBO: fields.IdVelesGeoListaCbo,
        idUsuario: currentUser.IdUsuario,
        nome: nomeValidado,
        Cbos: finalCBOs,
      };

      await updateListaCboApi(payload);

      const updatedList = listaCboState.listasCbo.map((lista) =>
        lista.IdVelesGeoListaCBO === fields.IdVelesGeoListaCbo
          ? {
            ...lista,
            Nome: nomeValidado,
            CBOs: finalCBOs,
          }
          : lista
      );

      dispatch(setListaCboState({ listasCbo: updatedList }));
      setAlert({
        message: "Lista CBO atualizada com sucesso!",
        type: "success"
      });

      // ✅ Limpa dados temporários de arquivo (se usado para preview)
      dispatch(resetListaCboFileDataState());

      dispatch(setOpenEditListaCboState(false));
      dispatch(resetEditListaCboFormState());
    } catch (error: any) {
      console.error("Erro ao atualizar lista CBO:", error);
      setAlert({
        message: error?.response?.data?.error ?? "Erro ao salvar alterações. Tente novamente.",
        type: "error"
      });
    } finally {
      setLoadingState({
        id: loadingId,
        loading: false,
        message: "",
      });
    }
  }, [
    currentUser?.IdUsuario,
    listaCboState.editListaCboForm.fields,
    listaCboState.listasCbo,
    dispatch,
    updateListaCboApi,
  ]);
  //DELETE
  const deleteListaCbo = useCallback(
    async (IdVelesGeoListaCbo: number) => {
      if (!currentUser) {
        console.error("Usuário não autenticado");
        setAlert({
          message: "Usuário não autenticado",
          type: "error"
        });
        return;
      }

      const loadingId = Math.random().toString();
      try {
        setLoadingState({
          id: loadingId,
          loading: true,
          message: "Excluindo lista de CBO...",
        });

        await updateStatusListaCboApi({
          idVelesGeoListaCbo: IdVelesGeoListaCbo,
          idUsuario: Number(currentUser.IdUsuario),
          status: false,
        });

        const updatedList = listaCboState.listasCbo.filter(
          (item) => item.IdVelesGeoListaCBO !== IdVelesGeoListaCbo
        );
        dispatch(setListaCboState({ listasCbo: updatedList }));
      } catch (error: any) {
        console.error(
          "Erro DETALHADO ao excluir lista de CBO:",
          error.response?.datasetupListaCboState

        );
        console.error("Status:", error.response?.status);
        console.error("Headers:", error.response?.headers);

        setAlert({
          message: error?.response?.data?.error ?? "Erro ao excluir lista de CBO",
          type: "error"
        });
      } finally {
        setLoadingState({
          id: loadingId,
          loading: false,
          message: "",
        });
      }
    },
    [currentUser, listaCboState.listasCbo, dispatch, updateStatusListaCboApi]
  );

  // //RESET
  // const resetListaCbos = useCallback(() => {
  //   dispatch(resetListaCboState());
  // }, [dispatch]);

  const resetListaCbosFileData = useCallback(() => {
    dispatch(resetListaCboFileDataState());
  }, [dispatch]);

  // const resetForm = useCallback(() => {
  //   dispatch(resetEditListaCboFormState());
  // }, [dispatch]);

  //FILE
  const processCboFile = (data: { codigo: string }[]) => {

    const todosCodigos = data.map(item => item.codigo.toString().trim());

    // ✅ Separa válidos e inválidos
    const codigosValidos: string[] = [];
    const codigosInvalidos: string[] = [];

    todosCodigos.forEach(codigo => {
      // Remove traços e valida se tem 4-6 dígitos
      const codigoLimpo = codigo.replace(/-/g, '');
      if (/^\d{4,6}$/.test(codigoLimpo)) {
        codigosValidos.push(codigoLimpo); // Salva sem traço
      } else {
        codigosInvalidos.push(codigo);
      }
    });

    const cbosExistentes = listaCboState.editListaCboForm.fields.CBOs || [];
    const codigosNovos: string[] = [];
    const codigosDuplicados: string[] = [];

    codigosValidos.forEach(codigo => {
      if (cbosExistentes.includes(codigo)) {
        codigosDuplicados.push(codigo);
      } else {
        codigosNovos.push(codigo);
      }
    });

    dispatch(setListaCboFileValidDataListState({ values: codigosNovos }));
    dispatch(setListaCboFileInvalidDataListState({ values: codigosInvalidos }));
    dispatch(setListaCboFileRepeatedDataListState({ values: codigosDuplicados }));

    // ✅ Feedback para o usuário
    if (codigosNovos.length === 0) {
      setAlert({
        message: "Nenhum código CBO novo encontrado no arquivo.",
        type: "warning"
      });
      return;
    }

    let mensagem = `${codigosNovos.length} código(s) CBO novo(s) encontrado(s).`;
    if (codigosDuplicados.length > 0) {
      mensagem += ` ${codigosDuplicados.length} duplicado(s).`;
    }
    if (codigosInvalidos.length > 0) {
      mensagem += ` ${codigosInvalidos.length} inválido(s).`;
    }

    setAlert({
      message: mensagem,
      type: "success"
    });
  };

  const openImportFileListaCbos = useCallback(
    (open: boolean) => {
      dispatch(setOpenImportFileCboState(open));
    },
    [dispatch]
  );

  const openPreviewListaCbo = useCallback(
    (open: boolean) => {
      dispatch(setOpenPreviewListaCboState(open));
    },
    [dispatch]
  );

  const addFileListToListaCBO = useCallback(() => {
    // Adiciona os CBOs válidos do arquivo à lista atual
    const cbosValidos = listaCboState.fileListData.fileListValidTable.fileListValidData;
    const cbosAtuais = listaCboState.editListaCboForm.fields.CBOs || [];

    // Remove duplicatas e adiciona os novos
    const todosCbos = Array.from(new Set([...cbosAtuais, ...cbosValidos]));

    dispatch(setEditListaCboState({ cbos: todosCbos }));

    // Limpa os dados do arquivo
    dispatch(resetListaCboFileDataState());

    setAlert({
      message: `${cbosValidos.length} código(s) CBO adicionado(s) à lista.`,
      type: "success"
    });
  }, [listaCboState, dispatch]);

  return {
    //GET
    getListaCboById,
    //FILE
    processCboFile,
    openImportFileListaCbos,
    openPreviewListaCbo,
    addFileListToListaCBO,
    //PREVIEW
    // setOpenPreviewListaCbos,
    // closePreviewListaCbos,
    //CREATE
    openCreateListaCboForm,
    createListaCbos,
    setCreateListaCbosNome,
    setCreateListaCboFormIdUsuario,
    //EDIT
    updateListaCbo,
    setEditListaCboNome,
    setEditListaCboFields,
    selectedListaCboToEdit,
    openEditListaCboForm,
    closeEditListaCbo,

    //DELETE
    deleteListaCbo,
    // //RESET
    // resetForm,
    // resetListaCbos,
    resetListaCbosFileData,
    setupListasCbo,
    setOpenListaCboDialog,
  };
};
